/**
 * Fidget Critter: color recolor preview + lead-time note.
 * Works with snippets/fc-recolor.liquid. Recolors the main product photo in a <canvas>
 * using a precomputed body mask (luminance-preserving tint), never touching product data.
 */
const LEAD_NOTE = 'This color has about a one-week lead time before it ships.';
const STOCK_NOTE = 'In stock, ships fast';
const DEFAULT_NOTE =
  'Custom colors: colors we have on hand ship fast. If your color needs a filament order, please allow about one week before it ships.';

const chosenByProduct = new Map(); // productId -> color name (link mode)
const sourceCache = new Map();
const swatchColorCache = new Map();
const renderCache = new Map();

function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('fc-recolor: failed to load ' + url));
    img.src = url;
  });
}

function hexToRgb(hex) {
  const h = String(hex || '').replace('#', '').trim();
  if (h.length < 6) return null;
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

async function getSource(cfg) {
  if (sourceCache.has(cfg.src)) return sourceCache.get(cfg.src);
  const promise = (async () => {
    const [mask, base] = await Promise.all([loadImage(cfg.mask), loadImage(cfg.src)]);
    const w = mask.naturalWidth;
    const h = mask.naturalHeight;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(mask, 0, 0, w, h);
    const m = ctx.getImageData(0, 0, w, h).data;
    const alpha = new Float32Array(w * h);
    let y0 = h;
    let y1 = 0;
    for (let i = 0; i < w * h; i++) {
      const a = m[i * 4] / 255;
      alpha[i] = a;
      if (a > 0.5) {
        const y = (i / w) | 0;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(base, 0, 0, w, h);
    const baseData = ctx.getImageData(0, 0, w, h);
    return { w, h, alpha, baseData, y0, y1: Math.max(y1, y0 + 1), lref: cfg.lref || 100, lhi: cfg.lhi || 200 };
  })();
  sourceCache.set(cfg.src, promise);
  promise.catch(() => sourceCache.delete(cfg.src));
  return promise;
}

/** For image swatches (multi-color silks), sample two colors from the swatch image; fall back to the hex. */
async function swatchColors(entry) {
  const key = entry.name + '|' + entry.hex + '|' + entry.img;
  if (swatchColorCache.has(key)) return swatchColorCache.get(key);
  let colors = [hexToRgb(entry.hex) || [200, 200, 200]];
  if (entry.img) {
    try {
      const url = entry.img.startsWith('//') ? 'https:' + entry.img : entry.img;
      const img = await loadImage(url);
      const c = document.createElement('canvas');
      c.width = 16;
      c.height = 16;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, 16, 16);
      const px = (x, y) => Array.from(ctx.getImageData(x, y, 1, 1).data.slice(0, 3));
      const a = px(4, 3);
      const b = px(11, 12);
      const diff = Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]);
      colors = diff > 60 ? [a, b] : [a];
    } catch (e) {
      /* keep hex */
    }
  }
  swatchColorCache.set(key, colors);
  return colors;
}

async function renderRecolor(cfg, entry) {
  const key = cfg.src + '|' + entry.name;
  if (renderCache.has(key)) return renderCache.get(key);
  const promise = (async () => {
    const src = await getSource(cfg);
    const cols = (await swatchColors(entry)).map((c) => c.map((v) => v / 255));
    const { w, h, alpha, baseData, y0, y1, lref, lhi } = src;
    const out = new ImageData(new Uint8ClampedArray(baseData.data), w, h);
    const d = out.data;
    const smax = Math.max(lhi / lref, 1.05);
    for (let y = 0; y < h; y++) {
      let C = cols[0];
      if (cols.length > 1) {
        const t = Math.min(Math.max((y - y0) / (y1 - y0), 0), 1);
        C = [0, 1, 2].map((k) => cols[0][k] * (1 - t) + cols[1][k] * t);
      }
      const Cb = [C[0] * 0.9 + 0.05, C[1] * 0.9 + 0.05, C[2] * 0.9 + 0.05];
      for (let x = 0; x < w; x++) {
        const i = y * w + x;
        const a = alpha[i];
        if (a <= 0.003) continue;
        const p = i * 4;
        const r = d[p] / 255;
        const g = d[p + 1] / 255;
        const b = d[p + 2] / 255;
        const s = ((0.299 * r + 0.587 * g + 0.114 * b) * 255) / lref;
        let nr;
        let ng;
        let nb;
        if (s <= 1) {
          nr = Cb[0] * s;
          ng = Cb[1] * s;
          nb = Cb[2] * s;
        } else {
          const wgt = Math.pow(Math.min((s - 1) / (smax - 1), 1), 1.4) * 0.75;
          nr = Cb[0] + (1 - Cb[0]) * wgt;
          ng = Cb[1] + (1 - Cb[1]) * wgt;
          nb = Cb[2] + (1 - Cb[2]) * wgt;
        }
        d[p] = (r * (1 - a) + nr * a) * 255;
        d[p + 1] = (g * (1 - a) + ng * a) * 255;
        d[p + 2] = (b * (1 - a) + nb * a) * 255;
      }
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d').putImageData(out, 0, 0);
    const blob = await new Promise((res) => canvas.toBlob(res, 'image/jpeg', 0.9));
    return URL.createObjectURL(blob);
  })();
  renderCache.set(key, promise);
  promise.catch(() => renderCache.delete(key));
  return promise;
}

function parseJSON(text) {
  try {
    return text ? JSON.parse(text) : null;
  } catch (e) {
    return null;
  }
}

class FcRecolor extends HTMLElement {
  connectedCallback() {
    this.mode = this.dataset.mode;
    this.productId = this.dataset.productId;
    this.sources = parseJSON(this.dataset.sources) || {};
    this.override = parseJSON(this.dataset.override) || {};
    this.shells = (this.dataset.shells || '').split(',').filter(Boolean);
    // colors with a real variant photo (never recolored) + caption shown on recolored previews
    this.photos = (this.dataset.photos || '').split('|').filter(Boolean);
    this.caption = this.dataset.caption || '';
    this.entries = Array.from(this.querySelectorAll('[data-fc-swatch]')).map((el) => ({
      el,
      name: el.dataset.name,
      hex: el.dataset.hex,
      img: el.dataset.img,
      v1: el.dataset.v1,
      v2: el.dataset.v2,
      stock: el.dataset.stock === '1',
    }));
    this.section = this.closest('.shopify-section') || document.body;

    this.onClick = (event) => {
      const sw = event.target.closest('[data-fc-swatch]');
      // swatches marked data-fc-direct are plain links to a live product page: let the browser navigate
      if (sw && this.contains(sw) && sw.hasAttribute('data-fc-direct')) return;
      if (sw && this.contains(sw) && this.mode === 'link') {
        event.preventDefault();
        chosenByProduct.set(this.productId, sw.dataset.name);
        this.apply();
        return;
      }
      if (event.target.closest('[data-fc-reset]')) {
        chosenByProduct.delete(this.productId);
        this.apply();
      }
    };
    this.addEventListener('click', this.onClick);

    this.onChange = () => this.schedule();
    this.section.addEventListener('change', this.onChange);
    // re-apply when the theme swaps the media gallery / variant picker after a variant change
    this.observer = new MutationObserver((records) => {
      if (records.some((r) => !this.contains(r.target))) this.schedule();
    });
    this.observer.observe(this.section, { childList: true, subtree: true });
    this.apply();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.section?.removeEventListener('change', this.onChange);
    this.observer?.disconnect();
  }

  schedule() {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.apply(), 80);
  }

  selectedValue(optionName) {
    const input = this.section.querySelector(`variant-picker input[data-option-name="${optionName}"]:checked`);
    if (input) return input.value;
    const select = this.section.querySelector(`variant-picker select[name="options[${optionName}]"]`);
    return select ? select.value : null;
  }

  currentShell() {
    const shell = this.selectedValue('Shell');
    return shell || this.shells[0] || '*';
  }

  currentEntry() {
    const name = this.mode === 'link' ? chosenByProduct.get(this.productId) : this.selectedValue('Color');
    return name ? this.entries.find((e) => e.name === name) || null : null;
  }

  mainImage() {
    const gallery = this.section.querySelector('media-gallery');
    if (!gallery) return null;
    return gallery.querySelector('slideshow-slide .product-media img, .product-media img');
  }

  setImage(img, url, key) {
    if (!img || img.dataset.fcKey === key) return;
    if (!img.dataset.fcOrigSrc) {
      img.dataset.fcOrigSrc = img.getAttribute('src') || '';
      img.dataset.fcOrigSrcset = img.getAttribute('srcset') || '';
    }
    img.removeAttribute('srcset');
    img.src = url;
    img.dataset.fcKey = key;
    this.setCaption(img, key.indexOf('override|') !== 0);
  }

  setCaption(img, on) {
    const holder = img && img.parentElement;
    if (!holder) return;
    let cap = holder.querySelector(':scope > .fc-recolor-caption');
    if (!on || !this.caption) {
      if (cap) cap.remove();
      return;
    }
    if (cap) return;
    if (getComputedStyle(holder).position === 'static') holder.style.position = 'relative';
    cap = document.createElement('span');
    cap.className = 'fc-recolor-caption';
    cap.textContent = this.caption;
    cap.style.cssText = 'position:absolute;left:10px;top:10px;z-index:2;padding:4px 10px;border-radius:999px;background:rgba(0,0,0,.62);color:#fff;font-size:12px;line-height:1.2;font-weight:600;pointer-events:none;';
    holder.appendChild(cap);
  }

  restoreImage(img) {
    if (!img) return;
    this.setCaption(img, false);
    if (!img.dataset.fcKey) return;
    img.src = img.dataset.fcOrigSrc;
    if (img.dataset.fcOrigSrcset) img.setAttribute('srcset', img.dataset.fcOrigSrcset);
    delete img.dataset.fcKey;
  }

  async apply() {
    const token = (this.token = (this.token || 0) + 1);
    const entry = this.currentEntry();
    const shell = this.currentShell();
    const img = this.mainImage();

    // swatch state, chosen name, order link
    this.entries.forEach((e) => e.el.setAttribute('aria-current', entry && e === entry ? 'true' : 'false'));
    const chosen = this.querySelector('[data-fc-chosen]');
    if (chosen) {
      chosen.hidden = !entry;
      if (entry) {
        const nameEl = this.querySelector('[data-fc-name]');
        if (nameEl.textContent !== entry.name) nameEl.textContent = entry.name;
        const useSecond = this.shells.length > 1 && shell === this.shells[1] && entry.v2;
        const variant = useSecond ? entry.v2 : entry.v1;
        this.querySelector('[data-fc-order]').href = `${this.dataset.targetUrl}?variant=${variant}`;
      }
    }

    // lead-time note (stock-based, from live variant inventory when available)
    const note = this.querySelector('[data-fc-note]');
    if (note) {
      const text = entry ? (entry.stock ? STOCK_NOTE : LEAD_NOTE) : this.mode === 'link' ? DEFAULT_NOTE : '';
      if (note.textContent.trim() !== text) note.textContent = text;
      note.classList.toggle('fc-recolor__note--stock', Boolean(entry && entry.stock));
    }

    // main photo
    if (!img) return;
    const cfg = this.sources[shell] || this.sources['*'];
    if (entry && this.photos.includes(entry.name)) {
      // real photo for this color: let the theme's variant image show
      this.restoreImage(img);
    } else if (entry && cfg) {
      try {
        const url = await renderRecolor(cfg, entry);
        if (token !== this.token) return;
        this.setImage(this.mainImage(), url, `${cfg.src}|${entry.name}`);
      } catch (e) {
        console.warn(e);
      }
    } else if (this.override[shell]) {
      this.setImage(img, this.override[shell], `override|${shell}`);
    } else {
      this.restoreImage(img);
    }
  }
}

if (!customElements.get('fc-recolor')) customElements.define('fc-recolor', FcRecolor);
